import type { RefObject, TouchEvent } from "react";
import { useState } from "react";

type UseHorizontalSwipeReturnType = {
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: () => void;
  elementPositionX: number;
};

export const useHorizontalSwipe = <T extends HTMLElement>(
  containerRef: RefObject<T | null>
): UseHorizontalSwipeReturnType => {
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e: TouchEvent) => {
    if (!containerRef.current || containerRef.current.clientWidth >= containerRef.current.scrollWidth) return;
    setIsDragging(true);
    setStartX(e.touches[0].clientX - currentX);
  };

  const updatePosition = (newX: number) => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const itemsWidth = containerRef.current.scrollWidth;

    const minTranslateX = Math.min(0, containerWidth - itemsWidth);
    const maxTranslateX = 0;
    const newCurrentX = Math.min(maxTranslateX, Math.max(minTranslateX, newX));

    if (newCurrentX === currentX) return;
    setCurrentX(newCurrentX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !containerRef.current) return;

    const newX = e.touches[0].clientX - startX;
    updatePosition(newX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    elementPositionX: currentX,
  };
};
